/**
 * Excel → Form Builder converter (single form / single sheet).
 *
 * Ported from the legacy frontend importer so behaviour (control-type
 * inference, conditional detection, validations) matches exactly, but typed
 * against Works Lite's `FormDefinition`.
 */

import * as XLSX from 'xlsx';
import { ConditionalType } from '../components/form-builder/types';
import type { ControlOption, FormControl, FormSection } from '../components/form-builder/types';
import type { ConversionResult, ExcelRow, ImportFormMetadata } from './excelImportTypes';

/** Parse the first sheet of a file into trimmed rows. */
export const parseExcelFile = (file: File): Promise<ExcelRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          defval: '',
          blankrows: false,
        }) as ExcelRow[];
        resolve(trimRows(jsonData));
      } catch (error: any) {
        reject(new Error(`Excel parsing failed: ${error.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsBinaryString(file);
  });
};

/** Trim whitespace from all header keys of every row. */
export const trimRows = (rows: ExcelRow[]): ExcelRow[] =>
  rows.map((row) => {
    const trimmedRow: any = {};
    Object.keys(row).forEach((key) => {
      trimmedRow[key.trim()] = (row as any)[key];
    });
    return trimmedRow as ExcelRow;
  });

const detectMandatoryFromText = (questionText: string): boolean =>
  (questionText || '').toLowerCase().includes('(mandatory)');

const detectOptionalFromText = (questionText: string): boolean =>
  (questionText || '').toLowerCase().includes('(optional)');

const isNumericalQuestion = (questionText: string, answer?: string): boolean => {
  const q = (questionText || '').toLowerCase();
  const a = answer?.toLowerCase().trim() || '';

  if (q.includes('(x.xx)') || q.includes('(x.x)')) return true;
  if (q.match(/\(xx\)/i)) return true;
  if (a === 'x.xx' || a === 'xx' || a === 'x.x' || a === 'number') return true;

  const measurementUnits = [
    '(m)',
    '(cm)',
    '(mm)',
    '(km)',
    '(ft)',
    '(in)',
    '(kg)',
    '(g)',
    '(lb)',
    '(oz)',
    '(l)',
    '(ml)',
    '(gal)',
    'length',
    'width',
    'height',
    'depth',
    'distance',
    'weight',
    'volume',
    'quantity',
    'number of',
    'how many',
  ];
  if (measurementUnits.some((u) => q.includes(u))) return true;

  const numericalKeywords = [
    'enter number',
    'input number',
    'provide number',
    'count',
    'total',
    'sum',
    'amount',
  ];
  if (numericalKeywords.some((k) => q.includes(k))) return true;

  return false;
};

/**
 * A question asking for a phone number (CWL-132).
 *
 * Deliberately checked *before* isNumericalQuestion in inferControlType: a phone
 * number is not a numeric value (its leading zero is significant), and mobile keys
 * its UK mobile validation off `control_type === 'tel'`, so importing these as
 * `text` leaves them unvalidatable.
 */
const isContactNumberQuestion = (questionText: string): boolean => {
  const q = (questionText || '').toLowerCase();

  const contactNumberKeywords = [
    'contact number',
    'phone number',
    'mobile number',
    'telephone number',
    'cell number',
    'contact no',
  ];
  return contactNumberKeywords.some((k) => q.includes(k));
};

const detectDateTimeType = (questionText: string, answer?: string): string | null => {
  const q = (questionText || '').toLowerCase();
  const a = answer?.toLowerCase().trim() || '';

  if (a === 'date time' || a === 'datetime' || a === 'date & time') return 'datetime-local';
  if (a === 'date' || a === 'date field' || a === 'dd/mm/yyyy' || a === 'mm/dd/yyyy') return 'date';
  if (a === 'time' || a === 'time field' || a === 'hh:mm' || a === 'hh:mm:ss') return 'time';

  const dateTimePatterns = [
    'date and time',
    'date & time',
    'date/time',
    'datetime',
    'scheduled for',
    'appointment',
    'timestamp',
  ];
  if (dateTimePatterns.some((p) => q.includes(p))) return 'datetime-local';

  const datePatterns = [
    'date',
    'day',
    'when',
    'schedule',
    'deadline',
    'due date',
    'birth date',
    'dob',
    'expiry date',
    'expiration date',
    'start date',
    'end date',
    'completion date',
  ];
  const timePatterns = [
    ' time',
    'time ',
    'hour',
    'minute',
    'am',
    'pm',
    'start time',
    'end time',
    'duration',
    'scheduled time',
  ];

  const hasDate = datePatterns.some((p) => q.includes(p));
  const hasTime = timePatterns.some((p) => q.includes(p));

  if (hasDate && hasTime) return 'datetime-local';
  if (hasDate && !q.includes('time')) return 'date';
  if (hasTime && !q.includes('date')) return 'time';
  return null;
};

const inferControlType = (questionText: string, answer?: string): string => {
  const q = (questionText || '').toLowerCase();

  if (q.includes('signature')) return 'signature';
  if (q.includes('photo') || q.includes('image') || q.includes('picture')) return 's3_image';
  if (
    q.includes('pin drop') ||
    q.includes('coordinates') ||
    q.includes('location') ||
    q.includes('map')
  )
    return 'map';

  const dateTimeType = detectDateTimeType(questionText, answer);
  if (dateTimeType) return dateTimeType;

  if (isContactNumberQuestion(questionText)) return 'tel';

  if (isNumericalQuestion(questionText, answer)) return 'text';

  if (answer?.trim()) {
    const a = answer.toLowerCase().trim();
    if (a === 'yes/no') return 'radio';
    if (a === 'signature field') return 'signature';
    if (a === 'map') return 'map';
    if (a.includes('photo') || a.includes('image')) return 's3_image';

    const isFreeTextAnswer =
      a.includes('free text') || a === 'autopopulated/free text' || a.includes('autopopulated');
    if (isFreeTextAnswer) {
      const textareaKeywords = [
        'comment',
        'observation',
        'note',
        'description',
        'detail',
        'explain',
        'describe',
        'provide detail',
        'additional information',
        'reason',
        'remark',
        'feedback',
      ];
      const shouldBeTextarea = textareaKeywords.some((k) => q.includes(k));
      return shouldBeTextarea ? 'textarea' : 'text';
    }
  }

  return 'text';
};

const groupRowsBySectionAndQuestion = (rows: ExcelRow[]): Map<string, Map<string, ExcelRow[]>> => {
  const sections = new Map<string, Map<string, ExcelRow[]>>();
  let lastSectionOrder: string | null = null;
  let lastQuestionId: string | null = null;
  let autoCounter = 0;

  rows.forEach((row) => {
    let questionId = row.QuestionOrder || row['app q no.'];

    if (questionId) {
      lastQuestionId = questionId;
    } else if (lastQuestionId) {
      questionId = lastQuestionId;
      row.QuestionOrder = lastQuestionId;
    }

    if (!questionId && !row.questiontext?.trim()) return;

    if (!questionId) {
      const tempSection = row.SectionOrder || lastSectionOrder || '0';
      questionId = `auto_${tempSection}_${autoCounter++}`;
    }

    let sectionOrder = row.SectionOrder;
    if (!sectionOrder && lastSectionOrder) {
      sectionOrder = lastSectionOrder;
      row.SectionOrder = lastSectionOrder;
    } else if (sectionOrder) {
      lastSectionOrder = sectionOrder;
    }

    if (!sectionOrder) return;

    if (!sections.has(sectionOrder)) sections.set(sectionOrder, new Map<string, ExcelRow[]>());
    const sectionMap = sections.get(sectionOrder)!;
    if (!sectionMap.has(questionId)) sectionMap.set(questionId, []);
    sectionMap.get(questionId)!.push(row);
  });

  return sections;
};

/**
 * A NextQuestion cell of "END" (any case) means "finish the form here and go to
 * the summary" rather than naming a target question. Spreadsheets have no way to
 * express a null-with-intent, so a keyword is the only option on this path.
 */
const isEndFormTarget = (value?: string): boolean => (value ?? '').trim().toUpperCase() === 'END';

const isConditionalQuestion = (rows: ExcelRow[]): boolean => {
  if (rows.length === 0) return false;
  const firstRow = rows[0];

  if (firstRow.Answer?.toLowerCase() === 'yes/no') return true;

  if (rows.length === 1) {
    const questionText = firstRow.questiontext?.trim() || '';
    const answerLower = (firstRow.Answer?.trim() || '').toLowerCase();
    const conditionalAnswers = ['yes', 'no', 'n/a', 'na', 'true', 'false'];
    if (questionText.endsWith('?') && conditionalAnswers.includes(answerLower)) return true;
    if (conditionalAnswers.includes(answerLower) && firstRow.NextQuestion) return true;
  }

  if (rows.length > 1) {
    const rowsWithAnswers = rows.filter((r) => r.Answer?.trim());
    if (rowsWithAnswers.length > 1) {
      // "END" counts as a distinct destination here (one answer finishing the form
      // while another continues is exactly a branch), so it is normalised rather
      // than filtered out.
      const nextQuestions = new Set(
        rowsWithAnswers
          .filter((r) => r.NextQuestion?.trim())
          .map((r) => (isEndFormTarget(r.NextQuestion) ? 'END' : r.NextQuestion?.trim())),
      );
      if (nextQuestions.size > 1) return true;
      const answers = rowsWithAnswers.map((r) => r.Answer!.trim().toLowerCase());
      const conditionalAnswers = ['yes', 'no', 'n/a', 'na', 'true', 'false'];
      if (answers.every((ans) => conditionalAnswers.includes(ans))) return true;
    }
  }

  return false;
};

const createControlFromRows = (questionId: string, rows: ExcelRow[]): FormControl => {
  const firstRow = rows[0];
  const hasMultipleAnswers = rows.length > 1 && rows.some((r) => r.Answer);
  const isConditional = isConditionalQuestion(rows);

  let controlType: string;
  let conditionalType: ConditionalType | '' = '';

  if (isConditional) {
    controlType = 'conditional_field';
    conditionalType = ConditionalType.RADIO;
  } else if (hasMultipleAnswers) {
    controlType = 'option';
  } else {
    controlType = inferControlType(firstRow.questiontext, firstRow.Answer);
  }

  const options: ControlOption[] = [];
  const conditions: any[] = [];

  if (isConditional) {
    let answers: string[] = [];
    if (firstRow.Answer?.toLowerCase() === 'yes/no') {
      answers = ['Yes', 'No'];
    } else if (rows.length === 1) {
      const answerLower = (firstRow.Answer?.trim() || '').toLowerCase();
      if (['yes', 'no', 'n/a', 'na'].includes(answerLower)) {
        const q = firstRow.questiontext?.toLowerCase() || '';
        const hasNAOption =
          q.includes('n/a') || q.includes('not applicable') || q.includes('if applicable');
        answers = hasNAOption ? ['Yes', 'No', 'N/A'] : ['Yes', 'No'];
      } else {
        answers = [firstRow.Answer?.trim() || ''];
      }
    } else {
      answers = Array.from(
        new Set(rows.filter((r) => r.Answer?.trim()).map((r) => r.Answer!.trim())),
      );
    }

    answers.forEach((answer, index) => {
      const answerRow = rows.find((r) => r.Answer?.trim() === answer) || firstRow;
      conditions.push({
        condition_name: answer,
        condition_order: index,
        next_q_id: isEndFormTarget(answerRow.NextQuestion)
          ? null
          : answerRow.NextQuestion?.trim() || null,
        end_form: isEndFormTarget(answerRow.NextQuestion),
        conditionalControls: [],
      });
    });
  } else if (controlType === 'option' || controlType === 'radio' || controlType === 'checkbox') {
    rows.forEach((row) => {
      if (row.Answer?.trim()) {
        options.push({ option: row.Answer.trim(), svgIcon: '', svgIconName: '', color: '' });
      }
    });
  }

  if (controlType === 'radio' && firstRow.Answer?.toLowerCase() === 'yes/no' && !isConditional) {
    options.push({ option: 'Yes', enabled: false }, { option: 'No', enabled: false });
  }

  let required = false;
  if (detectMandatoryFromText(firstRow.questiontext)) {
    required = true;
  } else if (detectOptionalFromText(firstRow.questiontext)) {
    required = false;
  } else if (firstRow.Mandatory) {
    required = firstRow.Mandatory.toLowerCase() === 'y';
  }

  const isNumerical = isNumericalQuestion(firstRow.questiontext, firstRow.Answer);
  const data_type = isNumerical ? 'number' : '';

  let min = '';
  let max = '';
  let s3_bucket_name = '';
  let s3_folder_name = '';

  if (controlType === 's3_image') {
    s3_bucket_name = 'skewb-mobile';
    s3_folder_name = '/works';
    min = required ? '1' : '0';
    max = '10';
  } else if (controlType === 'signature') {
    s3_bucket_name = 'skewb-mobile';
    s3_folder_name = '/works';
  } else if (controlType === 'textarea') {
    min = '3';
    max = '250';
  } else if (controlType === 'long_textarea') {
    min = '3';
    max = '1000';
  } else if (controlType === 'text' && !isNumerical) {
    min = '3';
    max = '250';
  }

  const validations: any[] = [];
  if (required) {
    validations.push({
      name: 'required',
      label: 'Required',
      option: false,
      description: 'Please complete the required field before continuing',
    });
  }

  if (isNumerical && controlType === 'text') {
    validations.push(
      {
        name: 'min',
        label: 'Minimum',
        option: true,
        description: 'The value entered is below the minimum allowed value',
      },
      {
        name: 'max',
        label: 'Maximum',
        option: true,
        description: 'The value entered exceeds the maximum allowed value',
      },
      {
        name: 'pattern',
        label: 'Pattern',
        option: true,
        description: 'Please enter a valid number format',
      },
    );
  } else if (
    ['text', 'textarea', 'long_textarea', 'email', 'tel', 'url'].includes(controlType) &&
    !isNumerical
  ) {
    validations.push(
      {
        name: 'size',
        label: 'Size',
        option: true,
        description:
          'The correct input size for this field has not been met, please change the value and continue',
      },
      {
        name: 'maxlength',
        label: 'Max Length',
        option: true,
        description:
          'The maximum number of characters has been exceeded, please change the value and continue',
      },
      {
        name: 'pattern',
        label: 'Pattern',
        option: true,
        description:
          'specifies a regular expression that the input fields value is checked against, when the form is submitted',
      },
    );
  } else if (controlType === 's3_image') {
    validations.push(
      {
        name: 'size',
        label: 'Size',
        option: true,
        description:
          'The correct input size for this field has not been met, please change the value and continue',
      },
      {
        name: 'max',
        label: 'Max files',
        option: true,
        description:
          'The maximum number of items/images has been exceeded, please remove some and continue',
      },
      {
        name: 'min',
        label: 'Min files',
        option: true,
        description:
          'The minimum number of items/images has not been met, please add more and continue',
      },
    );
  } else if (['date', 'time', 'datetime-local'].includes(controlType)) {
    validations.push(
      {
        name: 'min',
        label: 'Minimum',
        option: true,
        description: 'The date/time entered is before the minimum allowed date/time',
      },
      {
        name: 'max',
        label: 'Maximum',
        option: true,
        description: 'The date/time entered is after the maximum allowed date/time',
      },
    );
  }

  const rowWithQuestion = rows.find((r) => r.questiontext?.trim()) || firstRow;
  const controlName = rowWithQuestion.questiontext?.trim() || `Question ${questionId}`;
  const endsForm = !isConditional && isEndFormTarget(firstRow.NextQuestion);
  const next_q_id = isConditional || endsForm ? null : firstRow.NextQuestion?.trim() || null;

  return {
    q_id: questionId,
    control_name: controlName,
    control_type: controlType,
    control_description: '',
    control_span_length: '',
    conditional_type: conditionalType,
    is_repeatable: false,
    required,
    min,
    max,
    order: null,
    prev_q_id: null,
    options,
    nested_controls: [],
    conditions,
    validations,
    next_q_id,
    end_form: endsForm,
    value: '',
    s3_bucket_name,
    s3_folder_name,
    field1_value: '',
    field2_value: '',
    operator: '',
    calculated_type: '',
    is_multi_select: false,
    data_type,
    help_text: '',
    max_repeat: 0,
    min_repeat: 0,
    group_name: '',
    group_description: '',
    group_controls: false,
  };
};

/** Convert one sheet's rows into a form definition. */
export const convertRowsToForm = (
  excelData: ExcelRow[],
  metadata: ImportFormMetadata & { name: string },
  currentUserId: string | number,
): ConversionResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    if (!excelData || excelData.length === 0) {
      errors.push('Sheet is empty');
      return { success: false, data: null, errors, warnings };
    }

    const sections = groupRowsBySectionAndQuestion(excelData);
    if (sections.size === 0) {
      errors.push('No valid sections found');
      return { success: false, data: null, errors, warnings };
    }

    const formSections: FormSection[] = [];
    let sequence = 1;

    const sortedSectionKeys = Array.from(sections.keys()).sort((a, b) => parseInt(a) - parseInt(b));

    sortedSectionKeys.forEach((sectionOrder) => {
      const questionMap = sections.get(sectionOrder)!;
      const controls: FormControl[] = [];
      const sortedQuestionKeys = Array.from(questionMap.keys()).sort(
        (a, b) => parseInt(a) - parseInt(b),
      );

      sortedQuestionKeys.forEach((questionId, index) => {
        const control = createControlFromRows(questionId, questionMap.get(questionId)!);
        if (index > 0) control.prev_q_id = sortedQuestionKeys[index - 1];
        controls.push(control);
      });

      const firstRow = excelData.find((r) => r.SectionOrder === sectionOrder);
      const sectionName = firstRow?.Form || `Section ${sectionOrder}`;

      formSections.push({
        id: null,
        rev_num: null,
        sequence: sequence++,
        section_name: sectionName,
        section_description: null,
        is_table_view: null,
        form_id: null,
        created_by: currentUserId,
        created_time: '',
        updated_by: '',
        updated_time: '',
        is_active: true,
        section_number: parseInt(sectionOrder),
        controls,
      });
    });

    const formJSON = {
      id: null,
      name: metadata.name,
      description: metadata.description,
      image_url: '',
      rev_num: null,
      created_by: currentUserId,
      created_time: '',
      updated_by: '',
      updated_time: '',
      is_delete: false,
      form_type_id: metadata.form_type_id,
      layout: metadata.layout,
      is_dynamic: metadata.is_dynamic,
      form: false,
      is_system: false,
      sections: formSections,
    };

    const totalRows = excelData.length;
    const processedQuestions = formSections.reduce((sum, s) => sum + s.controls.length, 0);
    if (processedQuestions < totalRows) {
      warnings.push(
        `${totalRows - processedQuestions} rows were skipped due to missing required fields`,
      );
    }

    return { success: true, data: formJSON, errors: [], warnings };
  } catch (error: any) {
    errors.push(`Conversion failed: ${error.message}`);
    return { success: false, data: null, errors, warnings };
  }
};

/** Validate that a sheet has the required columns. */
export const validateSheetStructure = (rows: ExcelRow[]): string[] => {
  const errors: string[] = [];
  if (!rows || rows.length === 0) {
    errors.push('Sheet is empty');
    return errors;
  }

  const firstRow = rows[0];
  ['Form', 'SectionOrder', 'questiontext'].forEach((col) => {
    if (!(col in firstRow)) errors.push(`Missing required column: ${col}`);
  });

  if (!('QuestionOrder' in firstRow) && !('app q no.' in firstRow)) {
    errors.push('Missing required column: QuestionOrder or app q no.');
  }

  return errors;
};
